/**
 * A gateway's update downloads (2026-10-19; docs/plans/2026-10-19_path-agnostic-signed-updates.md): when it may fetch
 * update packages for its cache (the agents behind it, and its own firmware), as the gateway detail sheet shows and
 * sets it, and what its update store holds and its own last firmware update, read from its birth. The window comes from
 * the plant's shift times (Plant time): without the shifts' end times, a gateway fetches at any time, slowly. Every
 * word a person reads is here. Pure.
 */
import type { GatewayCapabilities, GatewayUpdateFetch, UpdateFetchMode } from "@/lib/api/types";

/** The modes a person picks from, with what each is for. */
export const UPDATE_FETCH_MODES: { value: UpdateFetchMode; label: string; hint: string }[] = [
  {
    value: "off_shift",
    label: "Outside shifts (default)",
    hint: "For a gateway that stays on after the shift: it downloads while the plant is not working.",
  },
  {
    value: "during_shift",
    label: "During shifts",
    hint: "For a gateway that is switched off after the shift: it downloads while the plant works.",
  },
  { value: "any_time", label: "Any time", hint: "It downloads whenever an update is waiting." },
  { value: "off", label: "Never", hint: "It downloads nothing, so the PCs behind it take no update through it." },
];

/** The rate a person may set, in kbit/s (forge-protocol UpdateFetchMinKbps / MaxKbps). */
export const UPDATE_FETCH_MIN_KBPS = 16;
export const UPDATE_FETCH_MAX_KBPS = 100000;

/** The words of a mode, or the mode itself for one this page does not know. */
export function modeLabel(mode: string): string {
  return UPDATE_FETCH_MODES.find((m) => m.value === mode)?.label ?? mode;
}

/** A rate as typed: null for empty (the default rate), the number, or what is wrong with it. */
export function readKbps(text: string): { kbps: number | null; problem: string | null } {
  const t = text.trim();
  if (!t) return { kbps: null, problem: null };
  const n = Number(t);
  if (!Number.isInteger(n) || n < UPDATE_FETCH_MIN_KBPS || n > UPDATE_FETCH_MAX_KBPS) {
    return {
      kbps: null,
      problem: `Write the speed as a whole number of kbit/s from ${UPDATE_FETCH_MIN_KBPS} to ${UPDATE_FETCH_MAX_KBPS}, or leave it empty.`,
    };
  }
  return { kbps: n, problem: null };
}

/** A time on the plant's clock (its zone; the browser's when it has none), with the day when it is not today. */
export function plantClockWords(iso: string, zone: string, now: Date): string {
  const at = new Date(iso);
  const tz = zone.trim() || undefined;
  const fmt = (d: Date, opts: Intl.DateTimeFormatOptions) => {
    try {
      return new Intl.DateTimeFormat("en-GB", { ...opts, timeZone: tz }).format(d);
    } catch {
      return new Intl.DateTimeFormat("en-GB", opts).format(d);
    }
  };
  const time = fmt(at, { hour: "2-digit", minute: "2-digit", hour12: false });
  const day = fmt(at, { weekday: "short", day: "numeric", month: "short" });
  return day === fmt(now, { weekday: "short", day: "numeric", month: "short" }) ? `${time} today` : `${time} on ${day}`;
}

/** When the gateway may download, in one sentence. */
export function windowWords(v: GatewayUpdateFetch, now: Date): string {
  if (v.mode === "off") return "It downloads no updates.";
  if (v.mode === "any_time") return "It may download updates now.";
  if (!v.shift_ends) return "It may download at any time, slowly: the plant's shifts have no end times.";
  if (v.open_now) return "It may download updates now.";
  if (v.next_window) return `It may download from ${plantClockWords(v.next_window, v.timezone, now)} (plant time).`;
  return "It waits for its next window.";
}

/** Who set the policy and when, or that nobody did. */
export function setWords(v: GatewayUpdateFetch, now: Date): string {
  if (v.default || !v.set_at) return "Not set: the default.";
  const by = v.set_by === "ble" ? "on the gateway (Bluetooth)" : v.set_by === "console" ? "in the console" : "";
  const when = plantClockWords(v.set_at, v.timezone, now);
  return by ? `Set ${by}, ${when}.` : `Set ${when}.`;
}

/** One package in a gateway's update store, as its birth reports it (forge-protocol UpdateStoreEntry). */
export interface StoreEntry {
  product: string;
  target: string;
  version: string;
  size: number;
  haveBytes: number;
  whole: boolean;
  source: string;
}

/** A gateway's own last firmware update, as its birth reports it (forge-protocol FirmwareUpdateState). */
export interface FirmwareUpdate {
  version: string;
  state: string;
  at: string | null;
  why: string | null;
}

const str = (v: unknown): string => (typeof v === "string" ? v.trim() : "");
const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : 0);

/** The birth's update store, read defensively: a gateway without the store sends none. */
export function readUpdateStore(caps: GatewayCapabilities | null | undefined): StoreEntry[] {
  const raw = caps?.update_store;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((e) => {
    if (typeof e !== "object" || e === null) return [];
    const o = e as Record<string, unknown>;
    const product = str(o.product);
    const version = str(o.version);
    if (!product || !version) return [];
    return [
      {
        product,
        target: str(o.target),
        version,
        size: num(o.size),
        haveBytes: num(o.have_bytes),
        whole: o.whole === true,
        source: str(o.source),
      },
    ];
  });
}

/** The birth's own firmware update, or null when it sends none. */
export function readFirmwareUpdate(caps: GatewayCapabilities | null | undefined): FirmwareUpdate | null {
  const raw = caps?.firmware_update;
  if (typeof raw !== "object" || raw === null) return null;
  const o = raw as Record<string, unknown>;
  const version = str(o.version);
  const state = str(o.state);
  if (!version || !state) return null;
  return { version, state, at: str(o.at) || null, why: str(o.why) || null };
}

const PRODUCT_WORDS: Record<string, string> = { "sahas-forge-agent": "PC agent", "sahas-gateway": "Gateway firmware" };
const SOURCE_WORDS: Record<string, string> = {
  cloud: "from the cloud",
  gateway: "from a gateway",
  ble: "from the app",
  pendrive: "from a pendrive",
};

/** A store entry in one line: what it is, and how much of it the gateway holds. */
export function storeEntryWords(e: StoreEntry): string {
  const what = `${PRODUCT_WORDS[e.product] ?? e.product} ${e.version}${e.target.endsWith("-scada") ? " (SCADA)" : ""}`;
  const from = SOURCE_WORDS[e.source] ? `, ${SOURCE_WORDS[e.source]}` : "";
  if (e.whole) return `${what}: ready${from}`;
  const pct = e.size > 0 ? Math.min(99, Math.floor((e.haveBytes / e.size) * 100)) : 0;
  return `${what}: ${pct}% downloaded${from}`;
}

/** The gateway's own firmware update in one sentence. */
export function firmwareWords(f: FirmwareUpdate): string {
  switch (f.state) {
    case "waiting":
      return `Firmware ${f.version} is ready and waits for the gateway's window to install.`;
    case "installed":
      return `Firmware ${f.version} installed itself.`;
    case "rolled_back":
      return `Firmware ${f.version} did not come online in time: the gateway put the old one back.${f.why ? ` ${f.why}` : ""}`;
    case "failed":
      return `Firmware ${f.version} could not be installed.${f.why ? ` ${f.why}` : ""}`;
  }
  return `Firmware ${f.version}: ${f.state}.`;
}
